import { useSession } from '@ventawiz/web-kit'
import { useMemo } from 'react'

import { useMyTimezones } from '@/hooks/queries/timezones'
import {
  browserTimezone,
  resolveTimezone,
  type ResolveInput,
  type ResolvedTimezone,
  type StoreZoneKind,
} from '@/lib/timezone'

export type ResolvedTimezoneState = ResolvedTimezone & {
  /** Every input the chain read, so a caller can ask what a level falls back to. */
  input: ResolveInput
  /** The store the page shows, with its zones, once they have loaded. */
  store: ResolveInput['store']
  /** True until the person's rows have loaded; the answer is provisional until then. */
  pending: boolean
}

/**
 * The zone for a page — from the person's rows in `/v1/me/timezones` and the
 * profile default on their session.
 *
 * The default is read from the session rather than from DV-Auth's profile
 * routes on purpose: this app's token is minted for its own audience and those
 * routes refuse it, while the session is read with the cookie.
 *
 * One cached read serves every page and the header, so asking twice is free.
 */
export function useResolvedTimezone({
  page,
  storeId,
  kind = 'store',
}: {
  page: string | null
  storeId?: number | undefined
  kind?: StoreZoneKind
}): ResolvedTimezoneState {
  const { session } = useSession()
  const profile = session?.principal?.timezone ?? undefined
  const mine = useMyTimezones()

  return useMemo(() => {
    const store =
      storeId === undefined ? undefined : mine.data?.stores?.find((s) => s.store_id === storeId)
    const input: ResolveInput = {
      page,
      storeId,
      kind,
      overrides: mine.data?.overrides ?? [],
      profile,
      store,
      browser: browserTimezone(),
    }
    return { ...resolveTimezone(input), input, store, pending: mine.isPending }
  }, [page, storeId, kind, profile, mine.data, mine.isPending])
}
